<script setup lang="ts">
import type { IProductButton } from '@/features/ProductCartButton/model/types/button';
import { useCartStore } from '@/entities/cart/index';
import { useUserStore } from '@/entities/user/index';
import { useModalsStore } from '@/app/store/modals';

import CartIcon from '@/shared/ui/Icons/Cart.vue';
import TrashIcon from '@/shared/ui/Icons/Trash.vue';
import PlusIcon from '@/shared/ui/Icons/Plus.vue';
import MinusIcon from '@/shared/ui/Icons/Minus.vue';
import { Loader } from '@/widgets/loader';
import { formatSum } from '@/shared/utils/formatSum';
import type { CartItem } from '@/entities/cart/model/types/cart';

const { t } = useI18n();

const props = defineProps<IProductButton>();

const userStore = useUserStore();
const cartStore = useCartStore();
const modalsStore = useModalsStore();

const { addToCart, removeFromCart, incrementAmount, decrementAmount } = cartStore;

const isShowAmount: Ref<boolean> = ref(false);
const isShowPreloader: Ref<boolean> = ref(false);

const currentCartItem = computed<CartItem>(() => {
  const cartItem = cartStore.getProductFromCartById(props.product.id);
  return cartItem || { product: props.product, quantity: 0, subtotal: 0 };
});

const isAuth = computed(() => userStore.isAuth);

const amount = computed(() => {
  return currentCartItem.value.quantity;
});

const sum = computed(() =>
  formatSum(
    currentCartItem.value.product.price_with_discount * currentCartItem.value.quantity,
  ),
);

const showPreloader = (): void => {
  isShowPreloader.value = true;

  setTimeout(() => {
    isShowPreloader.value = false;
  }, 500);
};

const incrementAmountHandler = async (): Promise<void> => {
  showPreloader();

  await incrementAmount(props.product.id);
};

const addToCartHandler = async (): Promise<void> => {
  await addToCart(props.product);
};

const addButtonHandler = async (): Promise<void> => {
  if (!isAuth.value) {
    modalsStore.setModalState('auth', true);
    return;
  }

  if (amount.value === 0) {
    await addToCartHandler();
  } else {
    await incrementAmountHandler();
  }
};

const decrementAmountHandler = async (): Promise<void> => {
  showPreloader();

  await decrementAmount(props.product.id);
};

const removeFromCartHandler = async (): Promise<void> => {
  await removeFromCart(props.product.id);
  isShowAmount.value = false;
};

const removeButtonHandler = async (): Promise<void> => {
  if (amount.value > 1) {
    await decrementAmountHandler();
  } else {
    await removeFromCartHandler();
  }
};
</script>

<template>
  <div :class="['product-cart-button', { _empty: !amount }]">
    <template v-if="isShowPreloader">
      <div class="product-cart-button__preloader">
        <Loader is-white />
      </div>
    </template>
    <template v-else>
      <button
        v-if="!amount"
        class="product-cart-button__button _cart"
        :aria-label="t('addToCart')"
        @click="addButtonHandler"
      >
        <CartIcon class="product-cart-button__icon _cart" />
        {{ t('toCart') }}
      </button>
      <template v-else>
        <button
          class="product-cart-button__button _remove"
          :aria-label="t('removeFromCart')"
          @click.prevent="removeButtonHandler"
        >
          <template v-if="amount === 1">
            <TrashIcon class="product-cart-button__icon" />
          </template>
          <template v-else>
            <MinusIcon class="product-cart-button__icon" />
          </template>
        </button>
        <div class="product-cart-button__amount-wrapper">
          <span class="product-cart-button__amount"
            >{{ amount }} {{ t('productAmount') }}</span
          >
          <span class="product-cart-button__sum"> {{ sum }} UZS </span>
        </div>
        <button
          :class="['product-cart-button__button _add', { _disabled: amount === 0 }]"
          :aria-label="t('increaseQuantity')"
          @click.prevent="addButtonHandler"
        >
          <PlusIcon class="product-cart-button__icon" />
        </button>
      </template>
    </template>
  </div>
</template>

<style lang="less">
.product-cart-button {
  display: inline-flex;
  width: 100%;
  background-color: @color-tango;
  border-radius: @size-x3;
  transition: all @A linear;

  &._empty {
    &:hover {
      opacity: 0.75;
    }
  }

  &__amount-wrapper {
    .flex-column(0px);

    justify-content: center;
    width: 100%;
    text-align: center;
  }

  &__amount,
  &__sum {
    font-weight: 600;
    line-height: 130%;
  }

  &__amount {
    font-size: 13px;
    color: #fff;
  }

  &__sum {
    font-size: 10px;
    color: rgba(255, 255, 255, 0.8);
  }

  &__preloader {
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: @size-x3 @size-x4;

    .lds-ring {
      width: 20px;
      height: 20px;
      margin: 0;

      div {
        width: 16px;
        height: 16px;
        border-width: 2px;
      }
    }
  }

  &__button {
    .button-reset();

    display: inline-flex;
    justify-content: center;
    align-items: center;
    vertical-align: top;
    gap: @size-x2;
    color: #fff;
    font-size: 14px;
    font-weight: 600;
    line-height: 115%;
    width: 100%;
    padding: @size-x3 @size-x4;
    height: 43px;
    white-space: nowrap;

    &._remove,
    &._add {
      width: 100%;
      max-width: 43px;
      padding: 0;
    }

    &._remove {
      border-right: 1px solid rgba(255, 255, 255, 0.5);
    }

    &._add {
      border-left: 1px solid rgba(255, 255, 255, 0.5);
    }
  }

  &__icon {
    width: 18px;
    height: 18px;

    &._cart {
      width: 13px;
      height: 13px;
    }

    path {
      fill: #fff;
    }
  }

  @media @1024 {
    &__button {
      height: 36px;

      &._remove,
      &._add {
        max-width: @size-x9;
      }
    }

    &__preloader {
      height: 35px;
    }

    &__icon {
      width: 13px;
      height: 13px;
    }
  }
}
</style>
